import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMusicians } from '../lib/api';
import { MapPin, Search, ChevronDown, Star, X, Music, SlidersHorizontal } from 'lucide-react';
import { ListingCard, CardRow, CardTitle, CardPrice, CardDivider, CardTags, CardMetric } from '../components/ListingCard';
import { cn } from '../lib/utils';
import DirectoryLayout, { useBreakpoint } from '../components/layouts/DirectoryLayout';
import MusicianDetailPage from './MusicianDetailPage';
import { GENRES, EVENTS } from '../constants/taxonomy';
import { INSTRUMENTS, RATE_BUCKETS, SORT_OPTIONS } from '../constants/profileContract';
import { GEAR_CATEGORIES, gearShortLabel, broughtCategories } from '../constants/gearCategories';
import { PageSkeleton, Bone, BoneCard } from '../components/PageSkeleton';

const RATE_OPTIONS = RATE_BUCKETS.musician;

export default function MusiciansPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const selectedId = params.id || null;

  const [musicians, setMusicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedGear, setSelectedGear] = useState([]);
  const [rateFilter, setRateFilter] = useState(null);
  const [highRatedOnly, setHighRatedOnly] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMusicians(); }, [sortBy]);

  const loadMusicians = async () => {
    setLoading(true);
    try {
      const p = {};
      if (sortBy) p.sort = sortBy;
      const response = await getMusicians(p);
      setMusicians(response.data);
    } catch (error) {
      console.error('Failed to load musicians');
    } finally {
      setLoading(false);
    }
  };

  // Close sort dropdown on outside click
  useEffect(() => {
    if (!sortDropdownOpen) return;
    const handler = (e) => { if (sortRef.current && !sortRef.current.contains(e.target)) setSortDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [sortDropdownOpen]);

  const toggleInstrument = (inst) => setSelectedInstruments((prev) => prev.includes(inst) ? prev.filter((i) => i !== inst) : [...prev, inst]);
  const toggleEventType = (t) => setSelectedEventTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  const toggleGenre = (g) => setSelectedGenres((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]);
  const toggleGear = (c) => setSelectedGear((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedInstruments([]);
    setSelectedEventTypes([]);
    setSelectedGenres([]);
    setSelectedGear([]);
    setRateFilter(null);
    setHighRatedOnly(false);
  };

  const hasActiveFilters = searchQuery || selectedInstruments.length > 0 || selectedEventTypes.length > 0 ||
    selectedGenres.length > 0 || selectedGear.length > 0 || rateFilter || highRatedOnly;

  const activeFilterCount = [
    selectedInstruments.length > 0 ? 1 : 0,
    selectedEventTypes.length > 0 ? 1 : 0,
    selectedGenres.length > 0 ? 1 : 0,
    selectedGear.length,
    rateFilter ? 1 : 0,
    highRatedOnly ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const filteredMusicians = useMemo(() => musicians.filter((m) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const hit = [m.displayName, m.city, m.primaryInstrument].some((v) => v?.toLowerCase().includes(q));
      if (!hit) return false;
    }

    if (selectedInstruments.length > 0) {
      const musicianInstruments = [m.primaryInstrument, ...(m.instruments || [])].filter(Boolean);
      const hasMatch = selectedInstruments.some((inst) =>
        musicianInstruments.some((mi) => mi.toLowerCase().includes(inst.toLowerCase()))
      );
      if (!hasMatch) return false;
    }

    if (selectedEventTypes.length > 0) {
      const hasMatch = selectedEventTypes.some((type) =>
        (m.eventTypes || []).some((et) => et.toLowerCase().includes(type.toLowerCase()))
      );
      if (!hasMatch) return false;
    }

    if (selectedGenres.length > 0) {
      const musicianGenres = (m.genres || []).map((g) => g.toLowerCase());
      const hasMatch = selectedGenres.some((genre) => musicianGenres.some((mg) => mg.includes(genre.toLowerCase())));
      if (!hasMatch) return false;
    }

    if (selectedGear.length > 0) {
      const brings = new Set(broughtCategories(m.gear_profile));
      if (!selectedGear.every((c) => brings.has(c))) return false;
    }

    if (rateFilter) {
      const rate = m.baseRate || m.rateAmount || 0;
      if (rateFilter.min !== undefined && rate < rateFilter.min) return false;
      if (rateFilter.max !== undefined && rate >= rateFilter.max) return false;
    }

    if (highRatedOnly) {
      const ok = (m.averageRating && m.averageRating >= 4.5) || (m.rebookRate && m.rebookRate >= 70);
      if (!ok) return false;
    }

    return true;
  }), [musicians, searchQuery, selectedInstruments, selectedEventTypes, selectedGenres, selectedGear, rateFilter, highRatedOnly]);

  const handleCardClick = (musicianId, e) => {
    if (isMobile) return;
    e.preventDefault();
    navigate(`/musicians/${musicianId}`);
  };

  /* ── Unified sort dropdown (same pattern as RequestsPage) ─── */
  const sortDropdown = (
    <div className="relative" ref={sortRef}>
      <button onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
        className="flex items-center gap-1.5 px-3.5 h-9 text-xs font-semibold uppercase tracking-wider transition-all"
        style={{ fontFamily: 'var(--s8-font-condensed)', color: 'var(--s8-text-muted)', border: '1px solid var(--s8-border-card)', borderRadius: 'var(--s8-radius-md)' }}
        data-testid="sort-dropdown-btn"
      >
        {SORT_OPTIONS.find((o) => o.key === sortBy)?.label || 'Best match'}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {sortDropdownOpen && (
        <div className="absolute left-0 top-full mt-1 z-50 py-1 min-w-[160px]" style={{ background: 'var(--s8-bg-surface)', border: '1px solid var(--s8-border-card)', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', borderRadius: 'var(--s8-radius-md)' }}>
          {SORT_OPTIONS.map((opt) => (
            <button key={opt.key || 'default'} onClick={() => { setSortBy(opt.key); setSortDropdownOpen(false); }}
              className="w-full text-left px-4 py-2 text-sm transition-colors"
              style={{ fontFamily: 'var(--s8-font-body)', color: sortBy === opt.key ? 'var(--s8-amber)' : 'var(--s8-text-body)', background: sortBy === opt.key ? 'rgba(200,146,62,0.06)' : 'transparent' }}
              data-testid={`sort-${opt.key || 'default'}`}
            >{opt.label}</button>
          ))}
        </div>
      )}
    </div>
  );

  /* ── Filter panel (shared desktop sidebar / tablet drawer / mobile sheet) ─── */
  const filterPanel = (
    <>
      {/* Search */}
      <div className="flex items-center gap-3 mb-3 browse-input w-full">
        <Search className="h-4 w-4 shrink-0 text-[var(--s8-text-muted)]" />
        <input type="text" placeholder="Search name, city, instrument..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-transparent border-none outline-none w-full text-[var(--s8-text-body)] placeholder:text-[var(--s8-text-muted)]"
          style={{ fontFamily: 'var(--s8-font-body)', fontSize: '14px', padding: 0 }} data-testid="search-input" />
      </div>

      {/* Instrument */}
      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Instrument</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {INSTRUMENTS.map((inst) => (
            <button key={inst} onClick={() => toggleInstrument(inst)}
              className={cn('browse-chip', selectedInstruments.includes(inst) && 'browse-chip-active')}
              data-testid={`filter-instrument-${inst.toLowerCase()}`}
            >{inst}</button>
          ))}
        </div>
      </div>

      {/* Events */}
      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Events</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {EVENTS.map((type) => (
            <button key={type} onClick={() => toggleEventType(type)}
              className={cn('browse-chip', selectedEventTypes.includes(type) && 'browse-chip-active')}
              data-testid={`filter-event-${type.toLowerCase()}`}
            >{type}</button>
          ))}
        </div>
      </div>

      {/* Genre */}
      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Genre</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {GENRES.map((genre) => (
            <button key={genre} onClick={() => toggleGenre(genre)}
              className={cn('browse-chip', selectedGenres.includes(genre) && 'browse-chip-active')}
              data-testid={`filter-genre-${genre.toLowerCase()}`}
            >{genre}</button>
          ))}
        </div>
      </div>

      {/* Gear — must bring their own */}
      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Gear · must bring their own</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {GEAR_CATEGORIES.map(({ value, label }) => (
            <button key={value} onClick={() => toggleGear(value)}
              className={cn('browse-chip', selectedGear.includes(value) && 'browse-chip-active')}
              data-testid={`filter-gear-${value}`}
            >{label}</button>
          ))}
        </div>
      </div>

      {/* Rate */}
      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Rate</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {RATE_OPTIONS.map((opt) => (
            <button key={opt.label} onClick={() => setRateFilter(rateFilter?.label === opt.label ? null : opt)}
              className={cn('browse-chip', rateFilter?.label === opt.label && 'browse-chip-active')}
              data-testid={`filter-rate-${opt.label}`}
            >{opt.label}</button>
          ))}
        </div>
      </div>

      {/* Quality + Clear */}
      <div className="sidebar-section">
        <div className="flex items-center gap-2">
          <button onClick={() => setHighRatedOnly(!highRatedOnly)}
            className={cn('browse-chip flex items-center gap-1.5', highRatedOnly && 'browse-chip-active')}
            data-testid="reliability-toggle"
          >
            <Star className={cn('h-3 w-3', highRatedOnly && 'fill-[var(--s8-accent)]')} />4.5+ rated
          </button>
          {hasActiveFilters && (
            <button onClick={clearFilters}
              className="browse-chip flex items-center gap-1.5 hover:text-[var(--red)] hover:border-[var(--red)]"
              style={{ color: 'var(--s8-text-muted)', borderColor: 'var(--s8-border-card)' }}
              data-testid="clear-filters-btn"
            >
              <X className="h-3 w-3" />Clear all
            </button>
          )}
        </div>
      </div>
    </>
  );

  /* ── Card list ─── */
  const cardList = loading ? (
    <PageSkeleton label="Loading musicians" className="space-y-3">
      {[1, 2, 3, 4].map((i) => (
        <BoneCard key={i} className="px-4 pt-5 pb-4">
          <div className="flex items-start justify-between gap-4">
            <Bone w="58%" h={22} />
            <Bone w={72} h={20} tone="amber" />
          </div>
          <Bone w="52%" h={14} className="mt-3" />
          <div className="mt-4 pt-3 flex items-center justify-between" style={{ borderTop: '1px solid var(--s8-b1-solid)' }}>
            <div className="flex gap-2"><Bone w={56} h={22} /><Bone w={72} h={22} /></div>
            <Bone w={88} h={14} />
          </div>
        </BoneCard>
      ))}
    </PageSkeleton>
  ) : filteredMusicians.length === 0 ? (
    <div className="text-center py-12">
      <Music className="h-5 w-5 text-[var(--s8-text-dim)] mx-auto mb-3" />
      <p className="text-[var(--s8-text-muted)] text-sm">No musicians match your filters</p>
      {hasActiveFilters && <button onClick={clearFilters} className="mt-3 text-[var(--s8-accent)] hover:text-[var(--s8-text-main)] text-sm">Clear All Filters</button>}
    </div>
  ) : (
    filteredMusicians.map((musician, index) => {
      const allInstruments = [musician.primaryInstrument, ...(musician.instruments || [])].filter(Boolean);
      const uniqueInstruments = [...new Set(allInstruments)].slice(0, 2);
      const rate = musician.baseRate || musician.rateAmount;
      const rateType = (musician.rateType || musician.onboardingRateType) === 'HOURLY' ? 'per hr' : 'per gig';
      const cardClass = !isMobile && selectedId === musician.id ? 's8-card--selected' : '';
      return (
        <ListingCard key={musician.id} to={`/musicians/${musician.id}`} index={index} cardClass={cardClass} testId={`musician-card-${musician.id}`}
          onClick={(e) => handleCardClick(musician.id, e)}
        >
          <CardRow><CardTitle>{musician.displayName}</CardTitle><CardPrice amount={rate ? `€${rate}` : null} sub={rate ? rateType : null} /></CardRow>
          <div className="s8-venue"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{musician.city}{musician.travelRadiusKm ? ` · ${musician.travelRadiusKm}km radius` : ''}</span></div>
          <CardDivider />
          <CardRow>
            <CardTags>
              {uniqueInstruments.map((inst) => <span key={inst} className="s8-tag s8-tag--accent">{inst}</span>)}
              {musician.hasOwnEquipment && <span className="s8-tag s8-tag--teal">Own Gear</span>}
              {broughtCategories(musician.gear_profile).slice(0, 4).map((cat) => (
                <span key={cat} className="s8-tag"
                  style={{ fontFamily: 'var(--s8-font-mono)', color: 'var(--green-ok, #5A9A6B)', borderColor: 'var(--green-ok, #5A9A6B)' }}
                >{gearShortLabel(cat)}</span>
              ))}
            </CardTags>
            <CardMetric>{musician.rebookRate >= 80 ? `${musician.rebookRate}% rebooked` : musician.stage8GigCount > 0 ? `${musician.stage8GigCount} gigs` : ''}</CardMetric>
          </CardRow>
        </ListingCard>
      );
    })
  );

  /* ── Empty state for detail pane ─── */
  const emptyState = (
    <div className="flex flex-col items-center justify-center h-full" style={{ color: 'var(--t3)' }}>
      <Music className="h-12 w-12 mb-4" style={{ color: 'var(--s8-amber)', opacity: 0.3 }} />
      <h2 style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: '22px', letterSpacing: '-0.01em', lineHeight: 1.15, color: 'var(--t1)' }}>Select a Musician to See Details</h2>
      <p className="mt-2" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '12px' }}>{filteredMusicians.length} available across the Netherlands</p>
    </div>
  );

  const detailPane = selectedId ? (
    <MusicianDetailPage profileId={selectedId} embedded key={selectedId} />
  ) : null;

  /* ═══════ MOBILE: selected id renders full detail page ═══════ */
  if (isMobile && selectedId) {
    return <div className="musicians-page"><MusicianDetailPage /></div>;
  }

  /* ═══════ DESKTOP / TABLET: Three-pane layout ═══════ */
  if (!isMobile) {
    return (
      <div className="musicians-page">
        <DirectoryLayout
          title="Musicians"
          count={filteredMusicians.length}
          filters={filterPanel}
          list={cardList}
          detail={detailPane}
          emptyState={emptyState}
          sortControl={sortDropdown}
          mobileContent={null}
        />
      </div>
    );
  }

  /* ═══════ MOBILE: single-column layout ═══════ */
  return (
    <div className="browse-page musicians-page px-4 sm:px-6 py-6 sm:py-8 pb-10">
      <div className="mb-6">
        <div className="flex items-baseline justify-between">
          <h1 className="browse-title">Musicians</h1>
          <span className="text-[var(--s8-text-muted)]" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '12px' }} data-testid="available-count">{loading ? ' ' : `${filteredMusicians.length} available`}</span>
        </div>
      </div>

      {/* Filters pill + sort */}
      <div className="flex items-center gap-3 mb-4" style={{ height: '44px' }}>
        <button onClick={() => setFilterSheetOpen(true)}
          className="flex items-center gap-2 px-3.5 h-9 text-xs font-semibold uppercase tracking-wider transition-all"
          style={{ fontFamily: 'var(--s8-font-condensed)', color: activeFilterCount > 0 ? 'var(--s8-amber)' : 'var(--s8-text-muted)', border: `1px solid ${activeFilterCount > 0 ? 'var(--s8-amber)' : 'var(--s8-border-card)'}`, background: activeFilterCount > 0 ? 'rgba(200,146,62,0.08)' : 'transparent', borderRadius: 'var(--s8-radius-md)' }}
          data-testid="filters-pill"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
        </button>
        {sortDropdown}
      </div>

      {filterSheetOpen && (
        <div className="fixed inset-0 z-50" data-testid="filter-sheet">
          <div className="absolute inset-0 bg-black/50" onClick={() => setFilterSheetOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 px-5 pt-5 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] animate-slide-up" style={{ background: 'var(--s8-bg-surface)', border: '1px solid var(--s8-border-card)', borderBottom: 'none', maxHeight: '75vh', overflowY: 'auto', borderTopLeftRadius: 'var(--s8-radius-xl)', borderTopRightRadius: 'var(--s8-radius-xl)' }}>
            <div className="flex items-center justify-between mb-5">
              <button onClick={clearFilters} className="text-xs transition-colors" style={{ fontFamily: 'var(--s8-font-body)', color: 'var(--s8-text-muted)' }} data-testid="filter-clear-all-mobile">Clear All</button>
              <span className="text-sm font-semibold" style={{ fontFamily: 'var(--s8-font-condensed)', color: 'var(--s8-text-main)', letterSpacing: '1px', textTransform: 'uppercase' }}>Filters</span>
              <button onClick={() => setFilterSheetOpen(false)} className="p-1" style={{ color: 'var(--s8-text-muted)' }}><X className="h-4 w-4" /></button>
            </div>
            {filterPanel}
            <button onClick={() => setFilterSheetOpen(false)} className="w-full py-3 text-sm font-semibold uppercase tracking-wider transition-all mt-4" style={{ fontFamily: 'var(--s8-font-condensed)', background: 'var(--s8-amber)', color: 'var(--bg)', borderRadius: 'var(--s8-radius-md)' }} data-testid="filter-apply-btn">Apply</button>
          </div>
        </div>
      )}

      <div className="browse-card-grid-wrapper">{cardList}</div>
    </div>
  );
}
